<template>
  <div class="auth-page">
    <header class="top-bar">
      <div class="brand-pill" :style="{ backgroundColor: logoBackgroundColor }">
        <img
          v-if="clientLogo"
          :src="clientLogo"
          alt="Client Logo"
          class="brand-image" />
        <img
          v-else
          src="@/../public/static/img/acrew-logo-with-effects-white.png"
          alt="ACREW Logo"
          class="brand-image default" />
      </div>
      <div class="top-login">
        <img src="@/../public/static/img/acrew-dash-pattern.png" alt="" class="dash-pattern" />
        <span>Login</span>
      </div>
    </header>
    <div class="content-area">
      <div class="card-wrapper">
        <div class="auth-card">
          <div class="heading">
            <img src="/static/img/hello-hand-icon.png" alt="Hello" class="hand-icon" />
            <div class="heading-text">
              <h1>Hello!</h1>
              <h1>Welcome.</h1>
            </div>
          </div>
          <div v-if="isLoggingIn" class="loading-overlay">
            <v-progress-circular indeterminate color="primary" size="50" />
          </div>
          <v-form ref="form" @submit.prevent="login" lazy-validation class="modern-form">
            <v-text-field v-model="username"
                          label="Email"
                          type="email"
                          :rules="[rules.required, rules.loginEmail]"
                          validate-on-blur
                          autocomplete="email"
                          dense
                          hide-details="auto"
                          class="line-input custom-filled" />
            <v-text-field v-model="password"
                          label="Password"
                          type="password"
                          :rules="[rules.required]"
                          validate-on-blur
                          autocomplete="current-password"
                          dense
                          hide-details="auto"
                          class="line-input custom-filled" />
            <div class="button-row">
              <v-btn class="btn-primary" type="submit" :disabled="isLoggingIn">Login</v-btn>
              <v-btn class="btn-secondary" @click="goToForgotPassword()" :disabled="isLoggingIn">Forgot Password</v-btn>
            </div>
            <v-btn class="btn-outline-primary full-width-btn" @click="goToSignUp()" :disabled="isLoggingIn">
              Sign Up
            </v-btn>
          </v-form>
        </div>
      </div>
    </div>
    <div v-if="clientColor" class="powered-by">
      <span>BROUGHT TO YOU BY</span>
      <img src="@/../public/static/img/acrew-logo-with-effects.png" alt="Logo" />
    </div>
  </div>
</template>
<script>
import DataService from '../../_services/DataService';
import ValidateService from '../../_services/ValidateService';
import { loadClientPreferences } from '../utils/UxUtils';
export default {
  name: 'Login',
  data() {
    return {
      clientLogo: null,
      clientColor: null,
      whiteBehindLogo: false,
      username: '',
      password: '',
      error: false,
      isLoggingIn: false,
      redirectURI: '',
      rules: ValidateService.validators,
    };
  },
  computed: {
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || '#11405c');
    },
  },
  methods: {
    isTwoFactorDisabled() {
      const hostname = window.location.hostname || '';
      const localHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname);
      return localHost;
    },
    goToSignUp() {
      this.$router.push('new_account');
    },
    goToForgotPassword() {
      this.$router.push({
        name: 'ForgotPassword',
        query: {
          username: this.username,
        },
      });
    },
    async login() {
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return;
      }

      if (this.isLoggingIn) return;
      
      this.isLoggingIn = true;
      this.error = false;

      try {
        const response = await DataService.signin(this.username, this.password);
        
        if (response.status === 200) {
          if (this.isTwoFactorDisabled() || response.data.skipTwoFactorDelivery) {
            const { user, token, selectedClientId, clientIds, role, reportingRole } = response.data;
            DataService.updateSessionUser(user);
            DataService.updateClientId(selectedClientId);
            DataService.updateSessionRole(role);
            DataService.updateSessionReportingRole(reportingRole);

            localStorage.removeItem('twoFactorToken');
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('clientIds', JSON.stringify(clientIds));
            localStorage.setItem('role', role);
            localStorage.setItem('reportingRole', reportingRole || '');
            localStorage.setItem('token', token);
            this.$router.push('/dashboard');
            return;
          }

          localStorage.setItem('twoFactorToken', response.data.token);
          this.$router.push({
            name: 'TwoFactorAuthentication',
            query: {
              sentSms: response.data.sentSms,
              redirectURI: this.redirectURI,
              username: this.username,
            },
          });
        } else {
          this.error = true;
          this.$root.$emit('raise_alert', { color: 'error', text: 'Wrong email/password combination.' });
        }
      } catch (error) {
        this.error = true;
        this.$root.$emit('raise_alert', { color: 'error', text: 'Network error. Please try again.' });
      } finally {
        this.isLoggingIn = false;
      }
    },
  },
  created() {
    const { redirectURI } = this.$route.query;
    const token = localStorage.getItem('token');

    if (token !== null) {
      if (redirectURI !== undefined) {
        window.location.href = redirectURI;
      } else {
        this.$router.push('/dashboard');
      }

      return;
    }
    // check for redirect
    if (redirectURI !== undefined) {
      // URI is automatically decoded, so re-encode to pass on to 2FA page
      this.redirectURI = encodeURIComponent(redirectURI);
    }
  },
  async mounted() {
    try {
      if (this.$route.query.clientId) {
        DataService.updateClientId(this.$route.query.clientId);
      }
      
      // Pre-populate username from query string and autofocus password if provided
      if (this.$route.query.username) {
        this.username = this.$route.query.username;
        this.$nextTick(() => {
          const passwordField = this.$el.querySelector('input[type="password"]');
          if (passwordField) {
            passwordField.focus();
          }
        });
      }
      
      await loadClientPreferences(this);
    } catch (error) {
      this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to initialize. Please refresh the page.' });
    }
  },
};
</script>

<style scoped lang="scss">
  /** Login.vue */
  @import '@/scss/variables.scss';
  @import '@/scss/auth/page.scss';
  
  .hand-icon {
    width: 48px;
    height: 48px;
  }

  .full-width-btn {
    width: 100%;
    margin-top: 5px;
  }
</style>

